import { useEffect, useRef } from 'react';

import { useSafeIsFocused } from '../useSafeIsFocused';

/**
 * A safer alternative to `useFocusEffect` from `@react-navigation/native`
 * that works in both react-navigation and expo-router contexts.
 *
 * Runs the effect when the screen gains focus and the cleanup when it loses it.
 */
export const useSafeFocusEffect = (effect: EffectCallback) => {
  // ─── Is Focused ──────────────────────────────────────────────────────────────

  const isFocused = useSafeIsFocused();

  // ─── Effect Ref ──────────────────────────────────────────────────────────────
  // Keep a stable ref so the effect can be updated without re-subscribing.

  const effectRef = useRef(effect);
  effectRef.current = effect;

  // ─── Focus Effect ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isFocused) return;

    return effectRef.current() ?? undefined;
  }, [isFocused]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type EffectCallback = () => undefined | void | (() => void);
